import { useState, useEffect, useCallback, useRef } from "react";
import {
  Video,
  Loader2,
  Upload,
  Trash2,
  Send,
  Clock,
  Film,
  X,
  Check,
  AlertCircle,
  Wand2,
} from "lucide-react";
import { colors, fonts, layout, surfaces } from "../../styles/theme";
import { SearchBar } from "../../components/ui";
import { useAuth } from "../../hooks/useAuth";
import {
  fetchVideoLibrary,
  fetchVideoLibraryItem,
  createVideoLibraryItem,
  updateVideoLibraryItem,
  deleteVideoLibraryItem,
  getVideoLibraryUploadToken,
  getLinkedInStatus,
  postToLinkedIn,
  processVideoLibraryItem,
} from "../../services/api";

const SOURCE_FILTERS = [
  { id: "", label: "All" },
  { id: "upload", label: "Uploaded" },
  { id: "zoom", label: "Zoom" },
  { id: "tv", label: "TV" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function fmtDuration(seconds) {
  if (!seconds) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function fmtFileSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

// ── Upload Drop Zone ──────────────────────────────────────────

const STATUS_LABELS = {
  "pending-processing": { label: "Queued", color: colors.warning, bg: "rgba(245,158,11,0.08)" },
  processing: { label: "Transcribing", color: colors.info, bg: "rgba(99,102,241,0.08)" },
  transcribed: { label: "Summarising", color: colors.info, bg: "rgba(99,102,241,0.08)" },
  "clips-ready": { label: "Ready", color: colors.success, bg: "rgba(16,185,129,0.08)" },
  error: { label: "Error", color: colors.danger, bg: "rgba(239,68,68,0.08)" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_LABELS[status];
  if (!cfg) return null;
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: 99,
        background: cfg.bg,
        color: cfg.color,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      {(status === "processing" || status === "transcribed" || status === "pending-processing") && (
        <Loader2 size={9} className="spin" />
      )}
      {cfg.label}
    </span>
  );
}

function UploadDropZone({ onUpload, uploading, progress }) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = useCallback(
    (files) => {
      const videoFiles = Array.from(files).filter((f) =>
        ["video/mp4", "video/webm", "video/quicktime"].includes(f.type),
      );
      if (videoFiles.length === 0) return;
      videoFiles.forEach((f) => onUpload(f));
    },
    [onUpload],
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={() => !uploading && inputRef.current?.click()}
      style={{
        border: `2px dashed ${dragOver ? colors.info : colors.border}`,
        borderRadius: 12,
        padding: "32px 24px",
        textAlign: "center",
        cursor: uploading ? "default" : "pointer",
        background: dragOver ? "rgba(99,102,241,0.04)" : colors.bgCard,
        transition: "all 0.15s",
        marginBottom: 24,
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime"
        multiple
        style={{ display: "none" }}
        onChange={(e) => handleFiles(e.target.files)}
      />
      {uploading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
          <Loader2 size={20} className="spin" />
          <span style={{ fontSize: 14, color: colors.textSecondary }}>
            Uploading{progress ? ` (${progress}%)` : "..."}
          </span>
        </div>
      ) : (
        <>
          <Upload size={28} color={colors.textSecondary} style={{ marginBottom: 8 }} />
          <p style={{ fontSize: 14, color: colors.textSecondary, margin: 0 }}>
            Drag & drop video files here, or click to browse
          </p>
          <p style={{ fontSize: 12, color: colors.textSecondary, margin: "4px 0 0", opacity: 0.7 }}>
            MP4, WebM, or MOV
          </p>
        </>
      )}
    </div>
  );
}

// ── Video Card ────────────────────────────────────────────────

function VideoCard({ item, onSelect, onDelete, selected }) {
  return (
    <div
      onClick={() => onSelect(item)}
      style={{
        background: colors.bgCard,
        border: `1px solid ${selected ? colors.info : colors.border}`,
        borderRadius: 10,
        padding: 16,
        cursor: "pointer",
        transition: "all 0.15s",
        position: "relative",
      }}
    >
      {/* Video thumbnail / placeholder */}
      <div
        style={{
          width: "100%",
          aspectRatio: "16/9",
          borderRadius: 8,
          background: "rgba(0,0,0,0.06)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 12,
          overflow: "hidden",
        }}
      >
        {item.blobUrl ? (
          <video
            src={item.blobUrl}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            muted
            preload="metadata"
          />
        ) : (
          <Film size={32} color={colors.textSecondary} style={{ opacity: 0.3 }} />
        )}
      </div>

      <div style={{ fontWeight: 600, fontSize: 14, color: colors.text, marginBottom: 4 }}>
        {item.title}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          fontSize: 12,
          color: colors.textSecondary,
        }}
      >
        {item.durationSeconds > 0 && (
          <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Clock size={11} /> {fmtDuration(item.durationSeconds)}
          </span>
        )}
        <span>{fmtDate(item.createdAt)}</span>
        {item.fileSize > 0 && <span>{fmtFileSize(item.fileSize)}</span>}
        <StatusBadge status={item.status} />
      </div>

      {item.tags?.length > 0 && (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 8 }}>
          {item.tags.map((tag) => (
            <span
              key={tag}
              style={{
                fontSize: 11,
                padding: "2px 8px",
                borderRadius: 99,
                background: colors.accentDim,
                color: colors.accent,
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Delete button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item);
        }}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          background: "rgba(255,255,255,0.9)",
          border: "none",
          borderRadius: 6,
          padding: 4,
          cursor: "pointer",
          opacity: 0.6,
          transition: "opacity 0.15s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.6)}
        title="Delete video"
      >
        <Trash2 size={14} color={colors.danger} />
      </button>
    </div>
  );
}

// ── LinkedIn Post Modal ───────────────────────────────────────

function LinkedInPostModal({ video, linkedInStatus, onClose, onPosted }) {
  const [text, setText] = useState("");
  const [targets, setTargets] = useState(["personal"]);
  const [posting, setPosting] = useState(false);
  const [result, setResult] = useState(null);

  const toggleTarget = (t) => {
    setTargets((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
    );
  };

  const handlePost = async () => {
    if (!text.trim() || targets.length === 0) return;
    setPosting(true);
    setResult(null);
    try {
      const res = await postToLinkedIn({
        text: text.trim(),
        videoId: video?.id || null,
        targets,
      });
      setResult(res);
      if (res.results?.every((r) => r.success)) {
        setTimeout(() => {
          onPosted();
          onClose();
        }, 1500);
      }
    } catch (err) {
      setResult({ error: err.message });
    } finally {
      setPosting(false);
    }
  };

  return (
    <div
      style={{
        ...surfaces.overlay,
        ...layout.rowCenter,
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.bgCard,
          borderRadius: 14,
          padding: 28,
          width: 520,
          maxWidth: "90vw",
          maxHeight: "80vh",
          overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <h2
            style={{
              fontFamily: fonts.heading,
              fontSize: 20,
              fontWeight: 700,
              color: colors.text,
              margin: 0,
            }}
          >
            Post to LinkedIn
          </h2>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}
          >
            <X size={18} color={colors.textSecondary} />
          </button>
        </div>

        {/* Video preview */}
        {video && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: 10,
              background: colors.bg,
              borderRadius: 8,
              marginBottom: 16,
            }}
          >
            <Film size={16} color={colors.info} />
            <span style={{ fontSize: 13, fontWeight: 600 }}>{video.title}</span>
          </div>
        )}

        {/* Post text */}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Write your post..."
          rows={5}
          style={{
            width: "100%",
            padding: 12,
            borderRadius: 8,
            border: `1px solid ${colors.border}`,
            fontFamily: fonts.body,
            fontSize: 14,
            resize: "vertical",
            outline: "none",
            boxSizing: "border-box",
          }}
        />
        <div
          style={{
            textAlign: "right",
            fontSize: 11,
            color: text.length > 3000 ? colors.danger : colors.textSecondary,
            marginTop: 4,
          }}
        >
          {text.length} / 3,000
        </div>

        {/* Targets */}
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: colors.text,
              marginBottom: 8,
            }}
          >
            Post as:
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {linkedInStatus?.personUrn && (
              <button
                onClick={() => toggleTarget("personal")}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: `1px solid ${targets.includes("personal") ? colors.info : colors.border}`,
                  background: targets.includes("personal")
                    ? "rgba(99,102,241,0.08)"
                    : "transparent",
                  color: colors.text,
                  fontSize: 13,
                  cursor: "pointer",
                  fontFamily: fonts.body,
                }}
              >
                {linkedInStatus.personName || "Personal Profile"}
              </button>
            )}
            {linkedInStatus?.organizationUrn && (
              <button
                onClick={() => toggleTarget("company")}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: `1px solid ${targets.includes("company") ? colors.info : colors.border}`,
                  background: targets.includes("company")
                    ? "rgba(99,102,241,0.08)"
                    : "transparent",
                  color: colors.text,
                  fontSize: 13,
                  cursor: "pointer",
                  fontFamily: fonts.body,
                }}
              >
                {linkedInStatus.organizationName || "Company Page"}
              </button>
            )}
          </div>
        </div>

        {/* Result feedback */}
        {result && (
          <div style={{ marginTop: 16 }}>
            {result.error ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: colors.danger,
                  fontSize: 13,
                }}
              >
                <AlertCircle size={14} /> {result.error}
              </div>
            ) : (
              result.results?.map((r, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 13,
                    color: r.success ? colors.success : colors.danger,
                    marginBottom: 4,
                  }}
                >
                  {r.success ? <Check size={14} /> : <AlertCircle size={14} />}
                  {r.target}: {r.success ? "Posted!" : r.error}
                </div>
              ))
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
          <button
            onClick={onClose}
            style={{
              padding: "9px 16px",
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              background: "transparent",
              color: colors.text,
              fontSize: 13,
              cursor: "pointer",
              fontFamily: fonts.body,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handlePost}
            disabled={posting || !text.trim() || targets.length === 0}
            style={{
              padding: "9px 16px",
              borderRadius: 8,
              border: "none",
              background: colors.accent,
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              cursor: posting ? "default" : "pointer",
              opacity: posting || !text.trim() || targets.length === 0 ? 0.5 : 1,
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontFamily: fonts.body,
            }}
          >
            {posting ? (
              <>
                <Loader2 size={14} className="spin" /> Posting...
              </>
            ) : (
              <>
                <Send size={14} /> Post
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Video Detail Panel ────────────────────────────────────────

function VideoDetail({ item, onClose, onUpdate, onPostToLinkedIn }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description || "");
  const [tagsInput, setTagsInput] = useState((item.tags || []).join(", "));
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState("info");

  const handleSave = async () => {
    setSaving(true);
    try {
      const tags = tagsInput
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      await updateVideoLibraryItem(item.id, { title, description, tags });
      onUpdate();
      setEditing(false);
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  };

  const handleProcess = async () => {
    setProcessing(true);
    try {
      await processVideoLibraryItem(item.id);
      onUpdate(); // refresh list + detail
    } catch {
      // silent
    } finally {
      setProcessing(false);
    }
  };

  const hasSummary = !!item.summary;
  const hasTranscript = !!item.transcript?.phrases?.length;
  const hasClips = item.clips?.length > 0;
  const isProcessing = ["pending-processing", "processing", "transcribed"].includes(item.status);
  const canProcess = item.blobName && !isProcessing && item.status !== "clips-ready";

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        width: 480,
        maxWidth: "90vw",
        background: colors.bgCard,
        borderLeft: `1px solid ${colors.border}`,
        boxShadow: "-4px 0 20px rgba(0,0,0,0.08)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "16px 20px",
          borderBottom: `1px solid ${colors.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h3
          style={{
            fontFamily: fonts.heading,
            fontSize: 16,
            fontWeight: 700,
            color: colors.text,
            margin: 0,
          }}
        >
          Video Details
        </h3>
        <button
          onClick={onClose}
          style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}
        >
          <X size={18} color={colors.textSecondary} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
        {/* Video player */}
        {item.blobUrl && (
          <video
            src={item.blobUrl}
            controls
            style={{
              width: "100%",
              borderRadius: 8,
              marginBottom: 16,
              background: "#000",
            }}
          />
        )}

        {editing ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title"
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: `1px solid ${colors.border}`,
                fontSize: 14,
                fontFamily: fonts.body,
                outline: "none",
              }}
            />
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description"
              rows={3}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: `1px solid ${colors.border}`,
                fontSize: 14,
                fontFamily: fonts.body,
                resize: "vertical",
                outline: "none",
              }}
            />
            <input
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="Tags (comma-separated)"
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: `1px solid ${colors.border}`,
                fontSize: 14,
                fontFamily: fonts.body,
                outline: "none",
              }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "none",
                  background: colors.accent,
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: fonts.body,
                }}
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={() => setEditing(false)}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: `1px solid ${colors.border}`,
                  background: "transparent",
                  color: colors.text,
                  fontSize: 13,
                  cursor: "pointer",
                  fontFamily: fonts.body,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <h2
              style={{
                fontFamily: fonts.heading,
                fontSize: 18,
                fontWeight: 700,
                color: colors.text,
                margin: "0 0 8px",
              }}
            >
              {item.title}
            </h2>
            {item.description && (
              <p style={{ fontSize: 14, color: colors.textSecondary, margin: "0 0 12px" }}>
                {item.description}
              </p>
            )}
            <div
              style={{
                display: "flex",
                gap: 16,
                fontSize: 12,
                color: colors.textSecondary,
                marginBottom: 16,
              }}
            >
              <span>{fmtDate(item.createdAt)}</span>
              {item.durationSeconds > 0 && (
                <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <Clock size={11} /> {fmtDuration(item.durationSeconds)}
                </span>
              )}
              {item.fileSize > 0 && <span>{fmtFileSize(item.fileSize)}</span>}
              <span
                style={{
                  padding: "1px 8px",
                  borderRadius: 99,
                  background: colors.accentDim,
                  fontSize: 11,
                }}
              >
                {item.source || "upload"}
              </span>
              <StatusBadge status={item.status} />
            </div>
            {item.tags?.length > 0 && (
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 16 }}>
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      fontSize: 11,
                      padding: "2px 8px",
                      borderRadius: 99,
                      background: colors.accentDim,
                      color: colors.accent,
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Tabs for Summary / Transcript / Clips */}
            {(hasSummary || hasTranscript || hasClips) && (
              <>
                <div
                  style={{
                    display: "flex",
                    gap: 0,
                    borderBottom: `1px solid ${colors.border}`,
                    marginBottom: 16,
                  }}
                >
                  {[
                    { id: "info", label: "Info" },
                    hasSummary && { id: "summary", label: "Summary" },
                    hasTranscript && { id: "transcript", label: "Transcript" },
                    hasClips && { id: "clips", label: `Clips (${item.clips.length})` },
                  ]
                    .filter(Boolean)
                    .map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        style={{
                          padding: "8px 14px",
                          fontSize: 13,
                          fontWeight: activeTab === tab.id ? 600 : 400,
                          color: activeTab === tab.id ? colors.accent : colors.textSecondary,
                          background: "none",
                          border: "none",
                          borderBottom: activeTab === tab.id
                            ? `2px solid ${colors.accent}`
                            : "2px solid transparent",
                          cursor: "pointer",
                          fontFamily: fonts.body,
                        }}
                      >
                        {tab.label}
                      </button>
                    ))}
                </div>

                {activeTab === "summary" && item.summary && (
                  <div style={{ fontSize: 13, color: colors.text, lineHeight: 1.6 }}>
                    <p style={{ whiteSpace: "pre-wrap", margin: "0 0 16px" }}>{item.summary.executive}</p>
                    {item.summary.keyTopics?.length > 0 && (
                      <>
                        <div style={{ fontWeight: 600, fontSize: 12, color: colors.textSecondary, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Key Topics
                        </div>
                        <ul style={{ margin: "0 0 16px", paddingLeft: 18 }}>
                          {item.summary.keyTopics.map((t, i) => (
                            <li key={i} style={{ marginBottom: 4 }}>
                              <strong>{t.topic}</strong>: {t.detail}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    {item.summary.actionItems?.length > 0 && (
                      <>
                        <div style={{ fontWeight: 600, fontSize: 12, color: colors.textSecondary, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Action Items
                        </div>
                        <ul style={{ margin: "0 0 16px", paddingLeft: 18 }}>
                          {item.summary.actionItems.map((a, i) => (
                            <li key={i} style={{ marginBottom: 4 }}>
                              {a.action}
                              {a.assignee && a.assignee !== "Unassigned" && (
                                <span style={{ color: colors.textSecondary }}> — {a.assignee}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                )}

                {activeTab === "transcript" && hasTranscript && (
                  <div style={{ fontSize: 13, maxHeight: 400, overflowY: "auto" }}>
                    {item.transcript.phrases.map((p, i) => (
                      <div key={i} style={{ marginBottom: 8, lineHeight: 1.5 }}>
                        <span style={{ fontWeight: 600, color: colors.accent, fontSize: 11, marginRight: 6 }}>
                          {p.speaker}
                        </span>
                        <span style={{ fontSize: 10, color: colors.textSecondary, marginRight: 6 }}>
                          {fmtDuration(p.startSeconds)}
                        </span>
                        <span style={{ color: colors.text }}>{p.text}</span>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === "clips" && hasClips && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {item.clips.map((clip) => (
                      <div
                        key={clip.id}
                        style={{
                          padding: 12,
                          borderRadius: 8,
                          border: `1px solid ${colors.border}`,
                          background: colors.bg,
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{clip.title}</div>
                        <div style={{ fontSize: 11, color: colors.textSecondary, marginBottom: 8 }}>
                          {clip.speaker} · {fmtDuration(clip.durationSeconds)}
                          {clip.aiCategory && ` · ${clip.aiCategory}`}
                        </div>
                        {clip.url && (
                          <video
                            src={clip.url}
                            controls
                            style={{ width: "100%", borderRadius: 6, maxHeight: 200 }}
                            preload="metadata"
                          />
                        )}
                        {clip.transcript && (
                          <p style={{ fontSize: 12, color: colors.textSecondary, margin: "8px 0 0", fontStyle: "italic" }}>
                            {clip.transcript}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Processing status */}
            {isProcessing && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "12px 16px",
                  borderRadius: 8,
                  background: "rgba(99,102,241,0.06)",
                  color: colors.info,
                  fontSize: 13,
                  marginTop: 12,
                }}
              >
                <Loader2 size={14} className="spin" />
                {item.status === "pending-processing" && "Queued for processing..."}
                {item.status === "processing" && "Transcribing audio..."}
                {item.status === "transcribed" && "Generating summary & clips..."}
              </div>
            )}

            {item.status === "error" && item.errorMessage && (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  padding: "12px 16px",
                  borderRadius: 8,
                  background: "rgba(239,68,68,0.06)",
                  color: colors.danger,
                  fontSize: 13,
                  marginTop: 12,
                }}
              >
                <AlertCircle size={14} style={{ marginTop: 2, flexShrink: 0 }} />
                {item.errorMessage}
              </div>
            )}
          </>
        )}
      </div>

      {/* Actions */}
      <div
        style={{
          padding: "12px 20px",
          borderTop: `1px solid ${colors.border}`,
          display: "flex",
          gap: 8,
        }}
      >
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              background: "transparent",
              color: colors.text,
              fontSize: 13,
              cursor: "pointer",
              fontFamily: fonts.body,
            }}
          >
            Edit
          </button>
        )}
        {canProcess && (
          <button
            onClick={handleProcess}
            disabled={processing}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "none",
              background: colors.accent,
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              cursor: processing ? "default" : "pointer",
              opacity: processing ? 0.6 : 1,
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontFamily: fonts.body,
            }}
          >
            {processing ? (
              <>
                <Loader2 size={13} className="spin" /> Processing...
              </>
            ) : (
              <>
                <Wand2 size={13} /> Transcribe & Clip
              </>
            )}
          </button>
        )}
        <button
          onClick={() => onPostToLinkedIn(item)}
          style={{
            padding: "8px 14px",
            borderRadius: 8,
            border: "none",
            background: "#0077b5",
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontFamily: fonts.body,
          }}
        >
          <Send size={13} /> Post to LinkedIn
        </button>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────

export default function VideoLibrary({ embedded = false }) {
  const { role } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [showLinkedInModal, setShowLinkedInModal] = useState(false);
  const [linkedInVideo, setLinkedInVideo] = useState(null);
  const [linkedInStatus, setLinkedInStatus] = useState(null);

  const loadVideos = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchVideoLibrary({ search, source: sourceFilter })
      .then((data) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, sourceFilter]);

  useEffect(() => {
    const timer = setTimeout(loadVideos, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [loadVideos]);

  // Load LinkedIn status once
  useEffect(() => {
    getLinkedInStatus()
      .then(setLinkedInStatus)
      .catch(() => {});
  }, []);

  const handleUpload = useCallback(
    async (file) => {
      setUploading(true);
      setUploadProgress(null);
      try {
        // Get SAS upload token
        const { sasUrl, blobName, containerName } = await getVideoLibraryUploadToken(
          file.name,
          file.type,
        );

        // Upload directly to blob storage
        await fetch(sasUrl, {
          method: "PUT",
          headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": file.type },
          body: file,
        });

        // Get video duration from file
        let durationSeconds = 0;
        try {
          const videoEl = document.createElement("video");
          videoEl.preload = "metadata";
          const objUrl = URL.createObjectURL(file);
          durationSeconds = await new Promise((resolve) => {
            videoEl.onloadedmetadata = () => {
              URL.revokeObjectURL(objUrl);
              resolve(Math.floor(videoEl.duration || 0));
            };
            videoEl.onerror = () => {
              URL.revokeObjectURL(objUrl);
              resolve(0);
            };
            videoEl.src = objUrl;
          });
        } catch {
          // duration detection not critical
        }

        // Create the metadata record
        const title = file.name.replace(/\.[^/.]+$/, ""); // strip extension for title
        await createVideoLibraryItem({
          title,
          source: "upload",
          blobName,
          containerName,
          durationSeconds,
          fileSize: file.size,
          mimeType: file.type,
        });

        loadVideos();
      } catch (err) {
        setError(`Upload failed: ${err.message}`);
      } finally {
        setUploading(false);
        setUploadProgress(null);
      }
    },
    [loadVideos],
  );

  const handleDelete = useCallback(
    async (item) => {
      if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
      try {
        await deleteVideoLibraryItem(item.id);
        if (selectedItem?.id === item.id) setSelectedItem(null);
        loadVideos();
      } catch (err) {
        setError(`Delete failed: ${err.message}`);
      }
    },
    [selectedItem, loadVideos],
  );

  const openLinkedInModal = useCallback(
    (video) => {
      if (!linkedInStatus?.connected) {
        setError("LinkedIn not connected. Go to Settings to connect your account.");
        return;
      }
      setLinkedInVideo(video || null);
      setShowLinkedInModal(true);
    },
    [linkedInStatus],
  );

  return (
    <div>
      {/* Header — hidden when embedded in Videos page */}
      {!embedded && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 24,
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: fonts.heading,
                fontSize: 28,
                fontWeight: 700,
                color: colors.text,
                marginBottom: 4,
              }}
            >
              Video Library
            </h1>
            <p
              style={{
                fontFamily: fonts.body,
                fontSize: 14,
                color: colors.textSecondary,
              }}
            >
              {loading ? "Loading..." : `${total} video${total !== 1 ? "s" : ""}`}
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* LinkedIn status indicator */}
            {linkedInStatus && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  borderRadius: 8,
                  background: linkedInStatus.connected
                    ? "rgba(16,185,129,0.08)"
                    : "rgba(239,68,68,0.08)",
                  fontSize: 12,
                  color: linkedInStatus.connected ? colors.success : colors.danger,
                }}
              >
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: linkedInStatus.connected ? colors.success : colors.danger,
                  }}
                />
                LinkedIn {linkedInStatus.connected ? "Connected" : "Disconnected"}
              </div>
            )}

            {/* Text-only LinkedIn post button */}
            <button
              onClick={() => openLinkedInModal(null)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                borderRadius: 8,
                border: "none",
                background: "#0077b5",
                color: "#fff",
                fontSize: 13,
                fontFamily: fonts.body,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              <Send size={14} />
              New Post
            </button>
          </div>
        </div>
      )}

      {/* Upload zone */}
      <UploadDropZone onUpload={handleUpload} uploading={uploading} progress={uploadProgress} />

      {/* Search + filters */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <div style={{ flex: 1 }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search videos..." />
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {SOURCE_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setSourceFilter(f.id)}
              style={{
                padding: "6px 12px",
                borderRadius: 8,
                border: `1px solid ${sourceFilter === f.id ? colors.accent : colors.border}`,
                background: sourceFilter === f.id ? colors.accentDim : "transparent",
                color: sourceFilter === f.id ? colors.accent : colors.textSecondary,
                fontSize: 12,
                cursor: "pointer",
                fontFamily: fonts.body,
                fontWeight: sourceFilter === f.id ? 600 : 400,
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            padding: "10px 16px",
            borderRadius: 8,
            background: "rgba(239,68,68,0.08)",
            color: colors.danger,
            fontSize: 13,
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <AlertCircle size={14} />
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              marginLeft: "auto",
              padding: 2,
            }}
          >
            <X size={14} color={colors.danger} />
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: 60,
            color: colors.textSecondary,
          }}
        >
          <Loader2 size={24} className="spin" />
        </div>
      )}

      {/* Empty state */}
      {!loading && items.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: 60,
            color: colors.textSecondary,
          }}
        >
          <Video size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>No videos yet</p>
          <p style={{ fontSize: 13, margin: "4px 0 0" }}>
            Upload a video to get started
          </p>
        </div>
      )}

      {/* Video grid */}
      {!loading && items.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: 16,
          }}
        >
          {items.map((item) => (
            <VideoCard
              key={item.id}
              item={item}
              selected={selectedItem?.id === item.id}
              onSelect={(v) => {
                // Fetch fresh data with SAS URLs
                fetchVideoLibraryItem(v.id)
                  .then(setSelectedItem)
                  .catch(() => setSelectedItem(v));
              }}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Detail panel */}
      {selectedItem && (
        <VideoDetail
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={() => {
            loadVideos();
            // Refresh detail panel too
            fetchVideoLibraryItem(selectedItem.id)
              .then(setSelectedItem)
              .catch(() => {});
          }}
          onPostToLinkedIn={openLinkedInModal}
        />
      )}

      {/* LinkedIn post modal */}
      {showLinkedInModal && (
        <LinkedInPostModal
          video={linkedInVideo}
          linkedInStatus={linkedInStatus}
          onClose={() => {
            setShowLinkedInModal(false);
            setLinkedInVideo(null);
          }}
          onPosted={loadVideos}
        />
      )}
    </div>
  );
}
